/**
 * Walkthrough registry — maps portal versions to slide arrays.
 *
 * To add slides for a new release:
 *   1. Create src/data/walkthroughs/{X.Y.Z}.js exporting a slide array.
 *   2. Import + register it below.
 *   3. Bump VITE_VERSION in .env (drives CURRENT_PORTAL_VERSION).
 *
 * Per-slide visibility is declared on the slide itself and applied by
 * collectSlides: `minRoleWeight` for a role floor, `is_cloud_only: true` for
 * features that only exist on the 5.0 cloud architecture, `is_grandstream: true`
 * for intercom features, and `is_ehr_integration: true` for EHR Tasks features.
 * A slide filtered out for
 * everyone is not a problem — WalkthroughGate bumps the user's portal_version
 * silently when nothing survives, so the modal never shows empty.
 */
import { compareVersion } from '../../utils/compareVersion.js';
import { getRoleWeight, getCampusConfig, getEhrInfo } from '../../services/session.js';
import slides_4_3_8 from './4.3.8.js';
import slides_4_3_9 from './4.3.9.js';
import slides_4_3_10 from './4.3.10.js';
import slides_4_3_11 from './4.3.11.js';

const WALKTHROUGHS = {
  '4.3.8': slides_4_3_8,
  '4.3.9': slides_4_3_9,
  '4.3.10': slides_4_3_10,
  '4.3.11': slides_4_3_11,
};

/**
 * Is the active campus on the 5.0 cloud architecture?
 *
 * `is_cloud` is a MySQL tinyint on the campus row, so it can arrive as 1/0, as
 * true/false, or as "1"/"0" after a JSON round trip. Boolean("0") is true, so the
 * string case has to go through Number() rather than a bare truthiness test.
 * Anything unrecognised reads as not-cloud, which errs toward hiding a slide
 * rather than showing a 4.0 community a feature it does not have.
 */
function isCloudCampus() {
  const value = getCampusConfig()?.is_cloud;
  if (value === true) return true;
  if (value === false || value == null || value === '') return false;
  const n = Number(value);
  return Number.isFinite(n) && n !== 0;
}

/**
 * Is the active campus a Grandstream (intercom) campus?
 *
 * The same check that shows the Call Logs report in NavigationV2 and Reports.jsx:
 * the campus config's `is_grandstream`, read as truthy.
 */
function isGrandstreamCampus() {
  return !!getCampusConfig()?.is_grandstream;
}

/**
 * Can this user reach the EHR Tasks page, and its History tab, on the active campus?
 *
 * The same checks the page is gated on: NavigationV2 shows EHR Tasks only when the
 * campus's EHR vendor is active and the role weight is above 10, and TasksEhrPage
 * sends an inactive campus back to the dashboard. The page draws its tab bar, where
 * History lives, only when the vendor is receiving resident or user data, so a
 * vendor receiving neither has no History to announce.
 */
function canSeeEhrTasks() {
  const ehr = getEhrInfo();
  return ehr.active && (ehr.receiving_resident || ehr.receiving_user);
}

/**
 * Should this slide be shown to the current user on the current campus?
 *
 * Independent gates, all of which must pass:
 *   - `minRoleWeight` — role weight floor (omit for "everyone").
 *   - `is_cloud_only` — when truthy, the slide describes a 5.0-only feature and is
 *     hidden on 4.0 campuses. Omitted or falsy means architecture-independent, so
 *     the default stays "show it".
 *   - `is_grandstream` — when truthy, shown only on Grandstream campuses.
 *   - `is_ehr_integration` — when truthy, shown only where EHR Tasks is available.
 *
 * There is deliberately no inverse flag (4.0-only): no release has needed one, and
 * a second flag would raise the question of what both together means. Add one only
 * when a slide actually calls for it.
 */
function isSlideVisible(slide, weight, cloudCampus) {
  if (weight < (slide.minRoleWeight ?? 0)) return false;
  if (slide.is_cloud_only && !cloudCampus) return false;
  if (slide.is_grandstream && !isGrandstreamCampus()) return false;
  if (slide.is_ehr_integration && !canSeeEhrTasks()) return false;
  return true;
}

/**
 * Collect all slides from versions strictly greater than `fromVersion`
 * and less than or equal to `toVersion`, then drop any the current user should
 * not see — role weight below the slide's floor, a 5.0-only feature on a 4.0
 * campus, an intercom feature off Grandstream, or EHR Tasks where it isn't available.
 *
 * Slides are returned in version order (oldest → newest); within a
 * version, the file's declared order is preserved.
 *
 * Role weight and campus are read from the session here rather than passed in,
 * matching how this function already worked: the caller (WalkthroughGate) only
 * knows about versions.
 *
 * @param {string} fromVersion — user's current portal_version (exclusive)
 * @param {string} toVersion   — current portal version (inclusive)
 * @returns {Array<object>} filtered slide array
 */
export function collectSlides(fromVersion, toVersion) {
  const versions = Object.keys(WALKTHROUGHS)
    .filter((v) => compareVersion(v, fromVersion) > 0 && compareVersion(v, toVersion) <= 0)
    .sort(compareVersion);

  const weight = getRoleWeight();
  const cloudCampus = isCloudCampus();
  const slides = [];
  for (const v of versions) {
    for (const slide of WALKTHROUGHS[v]) {
      if (isSlideVisible(slide, weight, cloudCampus)) slides.push(slide);
    }
  }
  return slides;
}
